import React, { useEffect, useState } from "react";
import TabPanel from "@mui/lab/TabPanel";
import TabContext from "@mui/lab/TabContext";
import DfrFlightMap from "./dfrFlightMap";
import DfrHeatMap from "./dfrHeatMap";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import LightModeIcon from "@mui/icons-material/LightMode";
import MenuIcon from "@mui/icons-material/Menu";
import Box from "@mui/material/Box";
import AppBar from "@mui/material/AppBar";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import Drawer from "@mui/material/Drawer";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import TextField from "@mui/material/TextField";
import Toolbar from "@mui/material/Toolbar";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import { ConvertISOToDate } from "./dfrUtil";
import { useDfrTheme } from "./dfrTheme";

export default function DfrDashboard() {
    const [dfrFlights, setDfrFlights] = useState([]);
    const [filteredDfrFlights, setFilteredDfrFlights] = useState([]);
    const [monthsInOrder, setMonthsInOrder] = useState([]);
    const [selectAllMonths, setSelectAllMonths] = useState(true);
    const [selectedMonth, setSelectedMonth] = useState(null);
    const [organization, setOrganization] = useState("");
    const [tab, setTab] = useState(1);
    const { mode, setMode } = useDfrTheme();

    const [navigationOpen, setNavigationOpen] = useState(false);
    const isDesktop = useMediaQuery("(min-width:900px)");

    useEffect(() => {
        const fetchData = () => {
            const requestOptions = {
                method: "GET",
                headers: { "Content-Type": "application/json" }
            };
            fetch("/api/dfr/get_published", requestOptions)
                .then((response) => (response.ok ? response.json() : Promise.reject(response)))
                .then((data) => setDfrFlights(data))
                .catch((err) => console.error("DFR Query Error", err.stack));
        };
        fetchData();
    }, []);

    useEffect(() => {
        if (!dfrFlights || dfrFlights.length === 0) {
            return;
        }

        const filtered = [];
        for (const dfr of dfrFlights) {
            const time = new Date(dfr.time_start);
            const passMonth = selectAllMonths || isSelectedMonth(time);
            const passOrg = !organization || dfr.organization_name?.toLowerCase().includes(organization.toLowerCase());
            if (passMonth && passOrg) {
                filtered.push(dfr);
            }
        }
        setFilteredDfrFlights(filtered.sort((a, b) => new Date(a.time_start).getTime() - new Date(b.time_start).getTime()));
    }, [dfrFlights, selectedMonth, organization]);

    // Get months data.
    useEffect(() => {
        const dfrPerMonth = {};
        for (const dfr of dfrFlights) {
            const time = new Date(dfr.time_start);
            const month = new Date(time.getFullYear(), time.getMonth(), 1);
            const count = !organization || dfr.organization_name?.toLowerCase().includes(organization.toLowerCase()) ? 1 : 0;

            if (month in dfrPerMonth) {
                dfrPerMonth[month] += count;
            } else {
                dfrPerMonth[month] = count;
            }
        }

        const date = new Date();
        const latestMonth = new Date(date.getFullYear(), date.getMonth(), 1);
        const earliestMonth = new Date(latestMonth);
        earliestMonth.setMonth(earliestMonth.getMonth() - 11);

        const monthsInOrder = [];
        let totalCount = 0;

        while (latestMonth >= earliestMonth) {
            const count = latestMonth in dfrPerMonth ? dfrPerMonth[latestMonth] : 0;
            totalCount += count;
            monthsInOrder.push({ month: new Date(latestMonth), length: count });
            latestMonth.setMonth(latestMonth.getMonth() - 1);
        }

        setMonthsInOrder([{ month: null, length: totalCount }, ...monthsInOrder]);
    }, [dfrFlights, organization]);

    const isSelectedMonth = (date) => {
        if (!date || !selectedMonth) {
            return false;
        }
        return date.getFullYear() === selectedMonth.getFullYear() && date.getMonth() === selectedMonth.getMonth();
    };

    const toggleTheme = () => {
        setMode(mode === "dark" ? "light" : "dark");
    };

    return (
        <Box sx={{ display: "flex", flexDirection: "column", height: "100%", width: "100%" }}>
            <AppBar
                position="relative"
                sx={{
                    backgroundColor: (theme) => theme.palette.background.toolbar,
                    zIndex: (theme) => theme.zIndex.drawer + 1,
                    transition: (theme) =>
                        theme.transitions.create(["width", "margin"], {
                            easing: theme.transitions.easing.sharp,
                            duration: theme.transitions.duration.leavingScreen
                        }),
                    ...(navigationOpen && {
                        transition: (theme) =>
                            theme.transitions.create(["width", "margin"], {
                                easing: theme.transitions.easing.sharp,
                                duration: theme.transitions.duration.enteringScreen
                            })
                    })
                }}
            >
                <Toolbar sx={{ display: "flex", gap: 2, px: 2 }}>
                    {!isDesktop && (
                        <IconButton color="inherit" onClick={() => setNavigationOpen((prev) => !prev)}>
                            <MenuIcon />
                        </IconButton>
                    )}
                    <Box
                        component="img"
                        alt="CAL.png"
                        src={mode === "dark" ? "./images/CAL+White-217w.png" : "./images/CAL+Blue-214w.png"}
                        sx={{ width: "64px", p: 0, m: 0 }}
                    />
                    <Typography variant="h6" color="text.primary">
                        Public Flight Dashboard
                    </Typography>
                    <IconButton onClick={toggleTheme} sx={{ ml: "auto" }}>
                        {mode === "dark" ? <DarkModeIcon sx={{ color: "text.primary" }} /> : <LightModeIcon sx={{ color: "text.primary" }} />}
                    </IconButton>
                    <Box component="img" alt="ODOT.webp" src="./images/ODOT.webp" sx={{ width: "180px", p: 0, m: 0 }} />
                </Toolbar>
            </AppBar>
            <Box sx={{ display: "flex", flexDirection: "row", height: "100%", overflowY: "hidden" }}>
                {isDesktop ? (
                    <DfrSideBar
                        organization={organization}
                        setOrganization={setOrganization}
                        monthsInOrder={monthsInOrder}
                        selectAllMonths={selectAllMonths}
                        setSelectAllMonths={setSelectAllMonths}
                        selectedMonth={selectedMonth}
                        setSelectedMonth={setSelectedMonth}
                        isSelectedMonth={isSelectedMonth}
                    />
                ) : (
                    <Drawer variant="temporary" open={navigationOpen} onClose={() => setNavigationOpen((prev) => !prev)}>
                        <DfrSideBar
                            organization={organization}
                            setOrganization={setOrganization}
                            monthsInOrder={monthsInOrder}
                            selectAllMonths={selectAllMonths}
                            setSelectAllMonths={setSelectAllMonths}
                            selectedMonth={selectedMonth}
                            setSelectedMonth={setSelectedMonth}
                            isSelectedMonth={isSelectedMonth}
                            open={navigationOpen}
                        />
                    </Drawer>
                )}
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        height: "100%",
                        flex: 1,
                        p: 3,
                        pt: 2,
                        overflowY: "hidden",
                        backgroundColor: (theme) => theme.palette.background.dashboard
                    }}
                >
                    <TabContext value={tab}>
                        <Tabs value={tab} onChange={(_, value) => setTab(value)} sx={{ ml: 3 }}>
                            <Tab label="Heat Map" value={1} />
                            <Tab label="Flights" value={2} />
                        </Tabs>
                        <Divider sx={{ mb: "20px" }} />
                        <TabPanel value={1} sx={{ height: "100%", p: 1, pt: 0, pr: 3 }}>
                            <Paper sx={{ height: "100%", p: 1, backgroundColor: (theme) => theme.palette.background.map, backgroundImage: "none" }}>
                                <DfrHeatMap dfrs={filteredDfrFlights} />
                            </Paper>
                        </TabPanel>
                        <TabPanel value={2} sx={{ p: 1, pt: 0, pr: 3, overflowY: "auto" }}>
                            <Grid container spacing="20px">
                                {filteredDfrFlights.map((dfr) => (
                                    <Grid key={dfr.flight_uuid} size={{ xs: 12, lg: 6 }} sx={{ maxWidth: "700px", height: "370px" }}>
                                        <Paper
                                            sx={{
                                                display: "flex",
                                                flexDirection: "column",
                                                height: "100%",
                                                p: 1,
                                                pb: 0,
                                                backgroundColor: (theme) => theme.palette.background.map,
                                                backgroundImage: "none"
                                            }}
                                        >
                                            <Box sx={{ flex: 1 }}>
                                                <DfrFlightMap dfr={dfr} />
                                            </Box>
                                            <Box sx={{ display: "flex", flexDirection: "column", m: 1, mt: 0 }}>
                                                <Box sx={{ display: "flex", flexDirection: "row", gap: "10px" }}>
                                                    <Tooltip title={ConvertISOToDate(dfr.time_start)}>
                                                        <TextField
                                                            label="Start Time"
                                                            value={ConvertISOToDate(dfr.time_start)}
                                                            variant="standard"
                                                            size="small"
                                                            margin="dense"
                                                            fullWidth
                                                            disabled
                                                        />
                                                    </Tooltip>
                                                    <Tooltip title={dfr.organization_name}>
                                                        <TextField
                                                            label="Organization"
                                                            value={dfr.organization_name}
                                                            variant="standard"
                                                            size="small"
                                                            margin="dense"
                                                            fullWidth
                                                            disabled
                                                        />
                                                    </Tooltip>
                                                    <Tooltip title={dfr.incident}>
                                                        <TextField
                                                            label="Case/Incident"
                                                            value={dfr.incident}
                                                            variant="standard"
                                                            size="small"
                                                            margin="dense"
                                                            fullWidth
                                                            disabled
                                                        />
                                                    </Tooltip>
                                                    <Tooltip title={dfr.location}>
                                                        <TextField
                                                            label="Location"
                                                            value={dfr.location}
                                                            variant="standard"
                                                            size="small"
                                                            margin="dense"
                                                            fullWidth
                                                            disabled
                                                        />
                                                    </Tooltip>
                                                </Box>
                                                <TextField
                                                    multiline
                                                    variant="standard"
                                                    label="Summary"
                                                    value={dfr.summary}
                                                    size="small"
                                                    margin="dense"
                                                    fullWidth
                                                    disabled
                                                />
                                            </Box>
                                        </Paper>
                                    </Grid>
                                ))}
                            </Grid>
                        </TabPanel>
                    </TabContext>
                </Box>
            </Box>
        </Box>
    );
}

export function DfrSideBar(props) {
    if (props.monthsInOrder.length === 0) {
        return <></>;
    }

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                height: "100%",
                width: "250px",
                p: 3,
                pt: 2,
                mt: props.open ? "60px" : 0,
                backgroundColor: (theme) => theme.palette.background.sidebar
            }}
        >
            <TextField
                label="Organization Filter"
                value={props.organization}
                onChange={(e) => props.setOrganization(e.target.value)}
                size="small"
                margin="dense"
                fullWidth
            />
            <Divider sx={{ my: "15px" }} />
            <Box sx={{ display: "flex", flexDirection: "column", height: "100%", overflowY: "auto" }}>
                <Button
                    onClick={() => {
                        props.setSelectAllMonths(true);
                        props.setSelectedMonth(null);
                    }}
                    variant={props.selectAllMonths ? "outlined" : "text"}
                    sx={{ textTransform: "none" }}
                >
                    All Months ({props.monthsInOrder[0].length})
                </Button>
                {props.monthsInOrder.slice(1).map(({ month, length }) => {
                    const formatted = month.toLocaleString("en-US", { month: "short", year: "numeric" });
                    return (
                        <Button
                            key={formatted}
                            onClick={() => {
                                props.setSelectAllMonths(false);
                                props.setSelectedMonth(month);
                            }}
                            variant={props.isSelectedMonth(month) ? "outlined" : "text"}
                            sx={{ textTransform: "none" }}
                        >
                            {formatted} ({length})
                        </Button>
                    );
                })}
            </Box>
        </Box>
    );
}
